import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  X,
  AlertCircle,
  Info,
} from 'lucide-react';
import type { DisputeType, WorkflowStage, WorkflowTransition } from '@/types/dispute-types';
import { WorkflowStageEditor } from './workflow-stage-editor';
import { WorkflowTransitionEditor } from './workflow-transition-editor';

interface DisputeTypeEditorProps {
  disputeType: DisputeType | null;
  open: boolean;
  onClose: () => void;
  onSave: (disputeType: DisputeType) => void;
}

export function DisputeTypeEditor({ disputeType, open, onClose, onSave }: DisputeTypeEditorProps) {
  const [currentTab, setCurrentTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [transitions, setTransitions] = useState<WorkflowTransition[]>([]);
  const [selectedStage, setSelectedStage] = useState<WorkflowStage | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<WorkflowTransition | null>(null);
  const [isStageEditorOpen, setIsStageEditorOpen] = useState(false);
  const [isTransitionEditorOpen, setIsTransitionEditorOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (disputeType) {
      setFormData({
        name: disputeType.name,
        description: disputeType.description,
        isActive: disputeType.isActive,
      });
      setStages(disputeType.workflow.stages);
      setTransitions(disputeType.workflow.transitions);
    } else {
      // Reset for new dispute type
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
      setStages([]);
      setTransitions([]);
    }
    setErrors({});
  }, [disputeType, open]);

  const validateBasicInfo = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateWorkflow = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Workflow stages are optional - dispute types can be created without stages
    // Check for duplicate stage orders only if there are stages
    if (stages.length > 0) {
      const orders = stages.map(x => x.order);
      if (new Set(orders).size !== orders.length) {
        newErrors.stages = 'Duplicate stage orders detected';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateBasicInfo() || !validateWorkflow()) {
      return;
    }

    const savedDisputeType: DisputeType = {
      id: disputeType?.id || '',
      ...formData,
      createdAt: disputeType?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: disputeType?.createdBy || 'Current User',
      usageCount: disputeType?.usageCount || 0,
      avgResolutionTime: disputeType?.avgResolutionTime || 0,
      successRate: disputeType?.successRate || 0,
      workflow: {
        id: disputeType?.workflow.id || `wf-${Date.now()}`,
        disputeTypeId: disputeType?.id || '',
        stages: stages.sort((a, b) => a.order - b.order),
        transitions,
      },
    };

    onSave(savedDisputeType);
  };

  const handleAddStage = () => {
    setSelectedStage(null);
    setIsStageEditorOpen(true);
  };

  const handleEditStage = (stage: WorkflowStage) => {
    setSelectedStage(stage);
    setIsStageEditorOpen(true);
  };

  const handleSaveStage = (stage: WorkflowStage) => {
    if (selectedStage) {
      // Update existing stage
      setStages(stages.map(s => s.id === stage.id ? stage : s));
    } else {
      // Add new stage
      const newStage: WorkflowStage = {
        ...stage,
        id: `stage-${Date.now()}`,
        order: stages.length + 1,
      };
      setStages([...stages, newStage]);
    }
    setIsStageEditorOpen(false);
  };

  const handleDeleteStage = (stageId: string) => {
    setStages(stages.filter(s => s.id !== stageId));
    // Remove transitions involving this stage
    setTransitions(transitions.filter(t => t.fromStageId !== stageId && t.toStageId !== stageId));
  };

  const handleMoveStage = (stageId: string, direction: 'up' | 'down') => {
    const index = stages.findIndex(s => s.id === stageId);
    if (index === -1) return;

    const newStages = [...stages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newStages.length) return;

    // Swap stages
    [newStages[index], newStages[targetIndex]] = [newStages[targetIndex], newStages[index]];

    // Update orders
    newStages.forEach((stage, idx) => {
      stage.order = idx + 1;
    });

    setStages(newStages);
  };

  const handleAddTransition = () => {
    setSelectedTransition(null);
    setIsTransitionEditorOpen(true);
  };

  const handleEditTransition = (transition: WorkflowTransition) => {
    setSelectedTransition(transition);
    setIsTransitionEditorOpen(true);
  };

  const handleSaveTransition = (transition: WorkflowTransition) => {
    if (selectedTransition) {
      // Update existing transition
      setTransitions(transitions.map(t => t.id === transition.id ? transition : t));
    } else {
      // Add new transition
      const newTransition: WorkflowTransition = {
        ...transition,
        id: `trans-${Date.now()}`,
      };
      setTransitions([...transitions, newTransition]);
    }
    setIsTransitionEditorOpen(false);
  };

  const handleDeleteTransition = (transitionId: string) => {
    setTransitions(transitions.filter(t => t.id !== transitionId));
  };

  const getStageColor = (color: string) => {
    return {
      backgroundColor: `${color}20`,
      borderColor: color,
    };
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {disputeType ? 'Edit Dispute Type' : 'Create New Dispute Type'}
            </DialogTitle>
            <DialogDescription>
              Configure the dispute type and define its workflow stages and transitions
            </DialogDescription>
          </DialogHeader>

          <Tabs value={currentTab} onValueChange={setCurrentTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Information</TabsTrigger>
              <TabsTrigger value="workflow">Workflow Stages</TabsTrigger>
              <TabsTrigger value="transitions">Stage Transitions</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 pr-4">
              {/* Tab 1: Basic Information */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Dispute Type Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      placeholder="e.g., Billing Error, Service Quality"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Description <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Describe when this dispute type should be used..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className={errors.description ? 'border-destructive' : ''}
                    />
                    {errors.description && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-0.5">
                      <Label htmlFor="active">Active Status</Label>
                      <p className="text-sm text-muted-foreground">
                        Enable this dispute type for use in the system
                      </p>
                    </div>
                    <Switch
                      id="active"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                  </div>

                  <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Getting Started</p>
                          <p className="text-xs text-muted-foreground">
                            After defining basic information, proceed to configure workflow stages and transitions.
                            Each dispute of this type will follow the workflow you define.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Tab 2: Workflow Stages */}
              <TabsContent value="workflow" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Workflow Stages</h3>
                    <p className="text-sm text-muted-foreground">
                      Define the stages a dispute goes through from submission to resolution
                    </p>
                  </div>
                  <Button onClick={handleAddStage} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Stage
                  </Button>
                </div>

                {errors.stages && (
                  <Card className="border-destructive">
                    <CardContent className="pt-4">
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {errors.stages}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {stages.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="pt-6 pb-6 text-center">
                      <p className="text-muted-foreground">
                        No workflow stages defined yet. Click "Add Stage" to create your first stage.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {stages.sort((a, b) => a.order - b.order).map((stage, index) => (
                      <Card
                        key={stage.id}
                        className="border-l-4"
                        style={{ borderLeftColor: stage.color }}
                      >
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline">Stage {stage.order}</Badge>
                                <h4 className="font-semibold">{stage.name}</h4>
                                {stage.properties.requiresApproval && (
                                  <Badge variant="secondary" className="text-xs">
                                    Requires Approval
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">
                                {stage.description}
                              </p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                {stage.properties.slaHours && (
                                  <Badge variant="outline">
                                    SLA: {stage.properties.slaHours}h
                                  </Badge>
                                )}
                                {stage.properties.autoEscalate && (
                                  <Badge variant="outline">Auto-escalate</Badge>
                                )}
                                {stage.notifications.length > 0 && (
                                  <Badge variant="outline">
                                    {stage.notifications.length} notification(s)
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveStage(stage.id, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveStage(stage.id, 'down')}
                                disabled={index === stages.length - 1}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditStage(stage)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteStage(stage.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tab 3: Stage Transitions */}
              <TabsContent value="transitions" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Stage Transitions</h3>
                    <p className="text-sm text-muted-foreground">
                      Define how disputes move between workflow stages
                    </p>
                  </div>
                  <Button onClick={handleAddTransition} size="sm" disabled={stages.length < 2}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Transition
                  </Button>
                </div>

                {stages.length < 2 && (
                  <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
                    <CardContent className="pt-4">
                      <p className="text-sm text-amber-800 dark:text-amber-400 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        You need at least 2 workflow stages to define transitions
                      </p>
                    </CardContent>
                  </Card>
                )}

                {transitions.length === 0 && stages.length >= 2 ? (
                  <Card className="border-dashed">
                    <CardContent className="pt-6 pb-6 text-center">
                      <p className="text-muted-foreground">
                        No transitions defined yet. Click "Add Transition" to create your first transition.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {transitions.map((transition) => {
                      const fromStage = stages.find(s => s.id === transition.fromStageId);
                      const toStage = stages.find(s => s.id === transition.toStageId);

                      return (
                        <Card key={transition.id}>
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold mb-2">{transition.name}</h4>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                                  <Badge
                                    variant="outline"
                                    style={fromStage ? getStageColor(fromStage.color) : {}}
                                  >
                                    {fromStage?.name || 'Unknown'}
                                  </Badge>
                                  <span>→</span>
                                  <Badge
                                    variant="outline"
                                    style={toStage ? getStageColor(toStage.color) : {}}
                                  >
                                    {toStage?.name || 'Unknown'}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  {transition.requiresComment && (
                                    <Badge variant="outline">Requires Comment</Badge>
                                  )}
                                  {transition.requiresApproval && (
                                    <Badge variant="outline">Requires Approval</Badge>
                                  )}
                                  {transition.autoTransition && (
                                    <Badge variant="outline">Auto-transition</Badge>
                                  )}
                                  {transition.conditions && transition.conditions.length > 0 && (
                                    <Badge variant="outline">
                                      {transition.conditions.length} condition(s)
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditTransition(transition)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteTransition(transition.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              {disputeType ? 'Update' : 'Create'} Dispute Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Editor Dialog */}
      {isStageEditorOpen && (
        <WorkflowStageEditor
          stage={selectedStage}
          existingStages={stages}
          open={isStageEditorOpen}
          onClose={() => setIsStageEditorOpen(false)}
          onSave={handleSaveStage}
        />
      )}

      {/* Transition Editor Dialog */}
      {isTransitionEditorOpen && (
        <WorkflowTransitionEditor
          transition={selectedTransition}
          stages={stages}
          open={isTransitionEditorOpen}
          onClose={() => setIsTransitionEditorOpen(false)}
          onSave={handleSaveTransition}
        />
      )}
    </>
  );
}
